import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, LabelList,
} from "recharts";
import {
  estimatedInteriorCost, estimatedExteriorCost, estimatedSystemsCost,
  estimatedRenovationCost, estimatedSoftCosts, estimatedProfit, formatCurrency,
} from "@/lib/calculations";
import { useCurrency } from "@/lib/CurrencyContext";

// Waterfall: start at Sale, subtract each cost, end on Profit.
// We implement with stacked bars: a transparent "base" sits underneath a coloured "delta".
export default function ProfitWaterfall({ project }) {
  const currency = useCurrency();
  const sale = Number(project?.target_sale_price) || 0;
  const purchase = Number(project?.purchase_price) || 0;

  let interior = estimatedInteriorCost(project);
  let exterior = estimatedExteriorCost(project);
  let systems  = estimatedSystemsCost(project);
  if (interior + exterior + systems === 0) {
    interior = estimatedRenovationCost(project);
  }
  const soft = estimatedSoftCosts(project);
  const profit = estimatedProfit(project);

  // Build waterfall steps
  const raw = [
    { name: "Sale",     value: sale,          type: "start" },
    { name: "Purchase", value: -purchase,     type: "cost" },
    { name: "Interior", value: -interior,     type: "cost" },
    { name: "Exterior", value: -exterior,     type: "cost" },
    { name: "Systems",  value: -systems,      type: "cost" },
    { name: "Soft",     value: -soft,         type: "cost" },
    { name: "Profit",   value: profit,        type: "end" },
  ].filter((s) => Math.abs(s.value) > 0 || s.type !== "cost");

  // Compute base (running sum before this step) and bar height for each row
  let running = 0;
  const data = raw.map((s) => {
    let base, bar;
    if (s.type === "start") {
      base = 0;
      bar = s.value;
      running = s.value;
    } else if (s.type === "end") {
      base = Math.min(0, s.value);     // profit bar starts at 0
      bar = Math.abs(s.value);
      running = s.value;
    } else {
      // cost: value is negative; bar goes downward from running to running+value
      const nextRunning = running + s.value;
      base = nextRunning;              // lower edge
      bar = -s.value;                  // positive height
      running = nextRunning;
    }
    return { name: s.name, base, bar, raw: s.value, type: s.type };
  });

  if (sale === 0 && purchase === 0) {
    return (
      <div className="fc-card p-6" data-testid="waterfall-empty">
        <div className="fc-label">Profit Waterfall</div>
        <div className="text-xs text-[#A8A29E] mt-6 text-center py-12">
          Enter purchase and target sale prices to visualise the deal.
        </div>
      </div>
    );
  }

  const colorFor = (row) => {
    if (row.type === "start") return "#57534E";              /* stone-600 */
    if (row.type === "end") return row.raw >= 0 ? "#059669" : "#DC2626";
    return "#A8A29E"; /* cost — stone-400 */
  };

  return (
    <div className="fc-card p-6" data-testid="profit-waterfall">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="fc-label">Profit Waterfall</div>
          <div className="fc-heading text-lg mt-1" style={{ color: profit >= 0 ? "#059669" : "#DC2626" }}>
            {formatCurrency(profit, currency)}
          </div>
        </div>
        <div className="text-[11px] text-[#A8A29E]">Sale → costs → profit</div>
      </div>

      <div className="h-[260px] -mx-2">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 18, right: 12, left: 4, bottom: 4 }}>
            <XAxis
              dataKey="name"
              tick={{ fill: "#78716C", fontSize: 11 }}
              axisLine={{ stroke: "rgba(0,0,0,0.08)" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#A8A29E", fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={60}
              tickFormatter={(v) => compactCurrency(v, currency)}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              contentStyle={{
                background: "#FFFFFF",
                border: "1px solid rgba(0,0,0,0.10)",
                borderRadius: 8,
                fontSize: 12,
                color: "#1C1C1C",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
              formatter={(_v, _n, item) => [formatCurrency(item.payload.raw, currency), item.payload.name]}
              labelFormatter={() => ""}
            />
            <ReferenceLine y={0} stroke="rgba(0,0,0,0.10)" />
            <Bar dataKey="base" stackId="a" fill="transparent" isAnimationActive={false} />
            <Bar dataKey="bar" stackId="a" radius={[4, 4, 4, 4]} isAnimationActive={false}>
              {data.map((row, i) => (
                <Cell key={row.name ?? `row-${i}`} fill={colorFor(row)} />
              ))}
              <LabelList
                dataKey="raw"
                position="top"
                fill="#57534E"
                fontSize={10}
                formatter={(v) => compactCurrency(v, currency)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function compactCurrency(v, currency) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  let short;
  if (abs >= 1_000_000) short = `${(abs / 1_000_000).toFixed(1)}M`;
  else if (abs >= 1_000) short = `${(abs / 1_000).toFixed(0)}k`;
  else short = `${abs.toFixed(0)}`;
  const code = currency || "USD";
  return `${sign}${code} ${short}`;
}
